import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { supabase } from "./supabase";

const Icon = ({ d, size = 20, color = "currentColor", strokeWidth = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const icons = {
  jobs:    "M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM16 3H8a2 2 0 0 0-2 2v2h12V5a2 2 0 0 0-2-2z",
  infra:   "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
  edu:     "M22 10v6M2 10l10-5 10 5-10 5zM6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5",
  housing: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 22V12h6v10",
  agri:    "M12 2a9 9 0 0 1 9 9c0 4-2.5 7.5-6 9M12 2a9 9 0 0 0-9 9c0 4 2.5 7.5 6 9M12 2v18",
  arts:    "M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z",
  comms:   "M1 6l10.5 7L22 6M1 6h20v12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V6z",
  water:   "M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z",
  science: "M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18",
  plus:    "M12 5v14M5 12h14",
};

const categories = [
  { key: "jobs",    label: "Jobs & Opportunity",   icon: icons.jobs    },
  { key: "infra",   label: "Infrastructure",        icon: icons.infra   },
  { key: "edu",     label: "Higher Education",      icon: icons.edu     },
  { key: "housing", label: "Housing",               icon: icons.housing },
  { key: "agri",    label: "Agriculture",           icon: icons.agri    },
  { key: "arts",    label: "Arts & Culture",        icon: icons.arts    },
  { key: "comms",   label: "Communications & Tech", icon: icons.comms   },
  { key: "water",   label: "Water & Sanitation",    icon: icons.water   },
  { key: "science", label: "Science & Innovation",  icon: icons.science },
];

const fmt = (n) => "R" + Number(n || 0).toLocaleString("en-ZA");

const statusStyles = {
  approved:       { bg: "rgba(46,125,50,0.08)", color: "#2E7D32", label: "Approved" },
  pending_review: { bg: "rgba(201,168,76,0.12)", color: "#8a6a1a", label: "Pending review" },
  flagged:        { bg: "rgba(192,57,43,0.1)",  color: "#C0392B", label: "Flagged" },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [hoveredCat, setHoveredCat] = useState(null);
  const [user, setUser] = useState(null);
  const [donations, setDonations] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyData();
  }, []);

  const fetchMyData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { setLoading(false); return; }
    setUser(user);

    const { data: donationData } = await supabase
      .from("donations")
      .select("*, projects(title, category)")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    const { data: projectData } = await supabase
      .from("projects")
      .select("*")
      .eq("created_by", user.id)
      .order("created_at", { ascending: false });

    setDonations(donationData || []);
    setMyProjects(projectData || []);
    setLoading(false);
  };

  const firstName = user?.user_metadata?.first_name || "there";
  const totalContributed = donations.reduce((sum, d) => sum + Number(d.amount || 0), 0);
  const uniqueProjects = [...new Map(donations.map((d) => [d.project_id, d])).values()];

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", color: "#1A1A2E", minHeight: "100vh" }}>

      <Navbar />

      <style>{`
        .dash-hero { padding: 56px 48px 48px; }
        .dash-section { padding: 48px; }
        .dash-steps { padding: 48px; }
        .dash-footer { padding: 24px 48px; }
        .dash-cat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)); gap: 10px; max-width: 860px; margin: 0 auto; }
        .dash-my-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; max-width: 700px; margin: 0 auto 28px; }
        .dash-my-list { max-width: 700px; margin: 0 auto; }
        @media (max-width: 768px) {
          .dash-hero { padding: 36px 20px 32px; }
          .dash-section { padding: 32px 20px; }
          .dash-steps { padding: 32px 20px; }
          .dash-footer { padding: 24px 20px; flex-direction: column; gap: 8px; text-align: center; }
          .dash-cat-grid { grid-template-columns: repeat(3, 1fr); gap: 8px; }
          .dash-my-grid { grid-template-columns: 1fr; gap: 10px; }
        }
      `}</style>

      {/* HERO — personalized */}
      <div className="dash-hero" style={{ background: "#fff", borderBottom: "1px solid #EDE8DF", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "rgba(27,58,107,0.07)", border: "1px solid rgba(27,58,107,0.15)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#1B3A6B", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>
          Your Dashboard
        </div>
        <h1 style={{ fontSize: "clamp(26px, 5vw, 42px)", fontWeight: 900, color: "#1A1A2E", letterSpacing: "-0.02em", margin: "0 0 12px", lineHeight: 1.1 }}>
          Welcome back, <span style={{ color: "#C9A84C" }}>{firstName}</span>
        </h1>
        <p style={{ fontSize: 15, color: "#888", lineHeight: 1.8, maxWidth: 480, margin: "0 auto 32px" }}>
          {donations.length > 0
            ? "Here's what your contributions are building."
            : "You haven't contributed to a project yet. When you do, your impact will show up here."}
        </p>

        {donations.length > 0 && (
          <div className="dash-my-grid">
            <div style={{ background: "#F5F0E8", border: "1px solid #EDE8DF", borderRadius: 14, padding: "18px" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#1B3A6B" }}>{fmt(totalContributed)}</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Total contributed</div>
            </div>
            <div style={{ background: "#F5F0E8", border: "1px solid #EDE8DF", borderRadius: 14, padding: "18px" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#1B3A6B" }}>{uniqueProjects.length}</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Projects backed</div>
            </div>
            <div style={{ background: "#F5F0E8", border: "1px solid #EDE8DF", borderRadius: 14, padding: "18px" }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#1B3A6B" }}>{donations.length}</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Contributions made</div>
            </div>
          </div>
        )}

        {!loading && donations.length > 0 && (
          <div className="dash-my-list" style={{ marginBottom: myProjects.length > 0 ? 32 : 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E", marginBottom: 12, textAlign: "left" }}>Your projects</div>
            {uniqueProjects.map((d, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#fff", border: "1px solid #EDE8DF", borderRadius: 12, marginBottom: 8 }}>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E" }}>{d.projects?.title || "Project"}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{d.projects?.category}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#1B3A6B" }}>{fmt(d.amount)}</div>
              </div>
            ))}
          </div>
        )}

        {/* MY PROPOSED PROJECTS */}
        {!loading && myProjects.length > 0 && (
          <div className="dash-my-list">
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E", marginBottom: 12, textAlign: "left" }}>Projects you've proposed</div>
            {myProjects.map((p) => {
              const style = statusStyles[p.status] || { bg: "rgba(136,136,136,0.1)", color: "#888", label: p.status };
              return (
                <div key={p.id} style={{ padding: "14px 16px", background: "#fff", border: "1px solid #EDE8DF", borderRadius: 12, marginBottom: 8, textAlign: "left" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1A1A2E" }}>{p.title}</div>
                    <div style={{ fontSize: 10, background: style.bg, color: style.color, borderRadius: 99, padding: "3px 10px", fontWeight: 700, whiteSpace: "nowrap" }}>
                      {style.label}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "#888" }}>{p.category} &middot; {p.location}</div>
                  {p.status === "pending_review" && (
                    <div style={{ fontSize: 11, color: "#8a6a1a", marginTop: 6, lineHeight: 1.6 }}>
                      Your proposal is being reviewed. We'll notify you once a decision is made.
                    </div>
                  )}
                  {p.status === "approved" && (
                    <>
                      <div style={{ fontSize: 11, color: "#2E7D32", marginTop: 6, marginBottom: 10, lineHeight: 1.6 }}>
                        Your project is live and visible to contributors.
                      </div>
                      <button
                        onClick={() => navigate("/submit-milestone")}
                        style={{ background: "#1B3A6B", border: "none", borderRadius: 99, padding: "8px 16px", color: "#fff", fontWeight: 700, fontSize: 11, cursor: "pointer", fontFamily: "var(--font-body)" }}
                      >
                        Submit a milestone
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CATEGORIES */}
      <div className="dash-section">
        <div style={{ fontSize: 22, fontWeight: 800, color: "#1A1A2E", textAlign: "center", marginBottom: 6, letterSpacing: "-0.01em" }}>
          What do you want to build?
        </div>
        <div style={{ fontSize: 13, color: "#888", textAlign: "center", marginBottom: 28 }}>
          Choose a category to explore active projects
        </div>
        <div className="dash-cat-grid">
          {categories.map((c) => (
            <div
              key={c.key}
              onClick={() => window.location.href = "/projects"}
              onMouseEnter={() => setHoveredCat(c.key)}
              onMouseLeave={() => setHoveredCat(null)}
              style={{ background: "#fff", border: `1px solid ${hoveredCat === c.key ? "#1B3A6B" : "#EDE8DF"}`, borderRadius: 14, padding: "16px 8px", textAlign: "center", cursor: "pointer", boxShadow: hoveredCat === c.key ? "0 2px 12px rgba(27,58,107,0.08)" : "none", transition: "all 0.2s" }}
            >
              <div style={{ width: 44, height: 44, background: "#F5F0E8", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
                <Icon d={c.icon} size={20} color="#1B3A6B" />
              </div>
              <div style={{ fontSize: 11, color: "#4A4A6A", fontWeight: 500, lineHeight: 1.4 }}>{c.label}</div>
            </div>
          ))}
          <div
            onClick={() => window.location.href = "/propose-project"}
            onMouseEnter={() => setHoveredCat("propose")}
            onMouseLeave={() => setHoveredCat(null)}
            style={{ background: hoveredCat === "propose" ? "rgba(201,168,76,0.05)" : "#fff", border: "1.5px dashed #C9A84C", borderRadius: 14, padding: "16px 8px", textAlign: "center", cursor: "pointer", transition: "all 0.2s" }}
          >
            <div style={{ width: 44, height: 44, background: "rgba(201,168,76,0.1)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
              <Icon d={icons.plus} size={20} color="#C9A84C" />
            </div>
            <div style={{ fontSize: 11, color: "#C9A84C", fontWeight: 600, lineHeight: 1.4 }}>Propose a project</div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="dash-footer" style={{ background: "#1B3A6B", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: "0.06em", color: "#fff" }}>
          FORGE <span style={{ color: "#C9A84C" }}>SA</span>
        </div>
        <div style={{ color: "rgba(184,212,232,0.4)", fontSize: 12 }}>&copy; 2026 Forge SA &middot; forge-sa.co.za</div>
      </footer>

    </div>
  );
}